/**
 * Cell popup component for displaying full cell content.
 * Opens when double-clicking a cell in the results grid.
 * Inspired by DBeaver's data viewer.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import { CellPopupData } from '../types';

interface CellPopupProps {
  open: boolean;
  data: CellPopupData | null;
  onClose: () => void;
}

const CellPopup: React.FC<CellPopupProps> = ({ open, data, onClose }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    if (data?.value !== null && data?.value !== undefined) {
      const textToCopy = formatValue(data.value);
      try {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleCloseCopied = () => {
    setCopied(false);
  };

  const formatValue = (value: any): string => {
    if (value === null) return 'NULL';
    if (value === undefined) return 'undefined';
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const isJsonObject = (value: any): boolean => {
    return typeof value === 'object' && value !== null;
  };

  if (!data) return null;

  const displayValue = formatValue(data.value);
  const isJson = isJsonObject(data.value);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '300px' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {data.columnName} (Row {data.rowIndex + 1})
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ position: 'relative' }}>
            {data.value === null ? (
              <Typography color="text.secondary" fontStyle="italic">
                NULL
              </Typography>
            ) : (
              <Box
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: '400px',
                  overflow: 'auto',
                  backgroundColor: isJson ? 'grey.100' : 'transparent',
                  p: isJson ? 2 : 0,
                  borderRadius: isJson ? 1 : 0,
                }}
              >
                {displayValue}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={handleCopy}
            variant="outlined"
          >
            Copy
          </Button>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={handleCloseCopied}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseCopied} severity="success" sx={{ width: '100%' }}>
          Copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

export default CellPopup;







